import { useEffect, useRef, useState } from "react"
import { motion } from "framer-motion"
import { fadeUp } from "../../../../utils/motion"
import { MapPin, Loader2, Check } from "lucide-react"

export default function StepPropertyDetails({ data, onChange, onNext }) {

  const [query, setQuery] = useState(data?.location ?? "")
  const [results, setResults] = useState([])
  const [beds, setBeds] = useState(data?.beds ?? 2)
  const [baths, setBaths] = useState(data?.baths ?? 1)

  const [locating, setLocating] = useState(false)
  const [success, setSuccess] = useState(false)
  const [locationError, setLocationError] = useState("")
  const [selected, setSelected] = useState(false)

  const debounce = useRef(null)

  /* ================= SYNC ================= */

  useEffect(() => {
    onChange?.({ ...data, beds, baths })
  }, [beds, baths])

  useEffect(() => {
    const max = Math.min(beds, 4)
    if (baths > max) setBaths(max)
  }, [beds])

  /* ================= SEARCH ================= */

  useEffect(() => {

    if (selected) return

    if (!query || query.length < 3) {
      setResults([])
      return
    }

    const controller = new AbortController()

    clearTimeout(debounce.current)

    debounce.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(query)}`,
          { signal: controller.signal }
        )
        const json = await res.json()
        setResults(json)
      } catch (e) {
        if (e.name !== "AbortError") console.error(e)
      }
    }, 400)

    return () => controller.abort()

  }, [query, selected])

  /* ================= SELECT ================= */

  function selectLocation(item) {

    const lat = Number(item.lat)
    const lng = Number(item.lon)
    const location = item.display_name

    setQuery(location)
    setResults([])
    setSelected(true)
    setLocationError("")

    onChange?.({
      ...data,
      location,
      lat,
      lng
    })
  }

  /* ================= GEO + FULL ADDRESS ================= */

  async function detectLocation() {

    if (!navigator.geolocation) {
      setLocationError("Geolocation not supported")
      return
    }

    setLocating(true)
    setSuccess(false)
    setLocationError("")
    setSelected(true)

    navigator.geolocation.getCurrentPosition(

      async (pos) => {
        const lat = pos.coords.latitude
        const lng = pos.coords.longitude

        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
          )
          const json = await res.json()

          const location = json.display_name || "Detected location"

          setQuery(location)

          onChange?.({
            ...data,
            location,
            lat,
            lng
          })

          setSuccess(true)
          setTimeout(() => setSuccess(false), 1200)

        } catch {
          setLocationError("Unable to fetch address")
        }

        setLocating(false)
      },

      (err) => {
        setLocating(false)
        setSelected(false)

        if (err.code === 1) setLocationError("Location access denied")
        else if (err.code === 2) setLocationError("Unable to detect location")
        else setLocationError("Something went wrong")
      },

      { enableHighAccuracy: true, timeout: 8000 }

    )
  }

  function handleInput(e) {
    setQuery(e.target.value)
    setSelected(false)
  }

  const bathMax = Math.min(beds, 4)

  return (

    <div className="max-w-content space-y-8">

      {/* HEADER */}
      <motion.div variants={fadeUp}>
        <h2 className="text-title font-extrabold text-text-primary">
          Property details
        </h2>
        <p className="text-body text-text-muted mt-2">
          Enter location and details to analyse performance.
        </p>
      </motion.div>

      {/* LOCATION */}
      <motion.div variants={fadeUp} className="relative">

        <div className="rounded-[20px] border border-border-soft bg-bg-surface px-4 py-3">
          <input
            value={query}
            onChange={handleInput}
            placeholder="Search city, area or address"
            className="w-full bg-transparent outline-none text-text-primary"
          />
        </div>

        {/* LOCATE */}
        <button
          onClick={detectLocation}
          disabled={locating}
          className="mt-3 flex items-center gap-2 text-small text-accent-gold"
        >
          {locating ? (
            <Loader2 size={14} className="animate-spin" />
          ) : success ? (
            <Check size={14} className="text-green-400" />
          ) : (
            <MapPin size={14} />
          )}

          {locating
            ? "Detecting location..."
            : success
              ? "Location detected"
              : "Locate me"}
        </button>

        {/* ERROR */}
        {locationError && (
          <p className="mt-2 text-micro text-red-400">
            {locationError}
          </p>
        )}

        {/* DROPDOWN */}
        {results.length > 0 && !selected && (
          <div className="absolute z-20 mt-2 w-full rounded-xl border border-border-soft bg-bg-surface shadow-soft overflow-hidden">
            {results.map((r) => (
              <button
                key={r.place_id}
                onClick={() => selectLocation(r)}
                className="block w-full text-left px-4 py-3 text-small hover:bg-bg-light"
              >
                {r.display_name}
              </button>
            ))}
          </div>
        )}

      </motion.div>

      {/* SLIDERS */}
      <motion.div variants={fadeUp} className="grid gap-8 md:grid-cols-2">

        <Slider label="Bedrooms" min={1} max={5} value={beds}
          display={beds === 5 ? "4+" : beds} onChange={setBeds} />

        <Slider label="Bathrooms" min={1} max={bathMax} value={baths}
          onChange={setBaths} />

      </motion.div>

      {/* CTA */}
      <motion.button
        variants={fadeUp}
        onClick={onNext}
        disabled={!data?.lat}
        className="w-full py-3 rounded-pill bg-brand-primary text-white font-semibold disabled:opacity-40"
      >
        Analyse property
      </motion.button>

    </div>
  )
}


/* SLIDER */

function Slider({ label, min, max, value, onChange, display }) {

  const pct = ((value - min) / (max - min)) * 100

  return (
    <div>

      <div className="flex justify-between mb-2">
        <span className="font-medium text-text-primary">{label}</span>

        <motion.span
          key={value}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-small font-semibold text-accent-gold"
        >
          {display ?? value}
        </motion.span>
      </div>

      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-brand-primary"
        style={{
          background: `linear-gradient(90deg,var(--brand-500) ${pct}%,var(--border-soft) ${pct}%)`
        }}
      />

    </div>
  )
}